import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Image as ImageIcon,
  Link as LinkIcon,
  Unlink,
  Undo2,
  Redo2,
  Table as TableIcon,
  BetweenHorizontalEnd,
  BetweenVerticalEnd,
  Rows3,
  Columns3,
  Trash2,
  Upload,
  Loader2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { uploadBlogImage } from '../../services/api';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Separator } from 'components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { cn } from 'lib/utils';

// ProseMirror content styles. These need child/pseudo selectors that Tailwind
// utilities can't express, so they live in a scoped stylesheet rendered once
// with the editor. Tailwind preflight strips list markers and heading sizes,
// so those are restored explicitly here.
const EDITOR_STYLES = `
.dfc-tiptap .ProseMirror { outline: none; min-height: var(--dfc-editor-min-height, 300px); }
.dfc-tiptap .ProseMirror p { margin: 0.5em 0; }
.dfc-tiptap .ProseMirror h1 { font-size: 2em; font-weight: 700; margin: 0.67em 0; }
.dfc-tiptap .ProseMirror h2 { font-size: 1.5em; font-weight: 700; margin: 0.75em 0; }
.dfc-tiptap .ProseMirror h3 { font-size: 1.17em; font-weight: 700; margin: 0.83em 0; }
.dfc-tiptap .ProseMirror ul { list-style: disc; padding-left: 1.5em; margin: 0.5em 0; }
.dfc-tiptap .ProseMirror ol { list-style: decimal; padding-left: 1.5em; margin: 0.5em 0; }
.dfc-tiptap .ProseMirror blockquote { border-left: 4px solid #1072CA; padding-left: 1em; margin: 0.5em 0; font-style: italic; color: #515151; }
.dfc-tiptap .ProseMirror pre { background: #f5f5f5; border-radius: 4px; padding: 0.75em 1em; font-family: monospace; font-size: 0.9em; overflow: auto; margin: 0.5em 0; }
.dfc-tiptap .ProseMirror code { background: #f5f5f5; border-radius: 2px; padding: 0.1em 0.3em; font-family: monospace; font-size: 0.9em; }
.dfc-tiptap .ProseMirror a { color: #1072CA; text-decoration: underline; }
.dfc-tiptap .ProseMirror img { max-width: 100%; height: auto; border-radius: 4px; margin: 1em 0; }
.dfc-tiptap .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); color: #adb5bd; pointer-events: none; float: left; height: 0; }
.dfc-tiptap .ProseMirror table { width: 100%; border-collapse: separate; border-spacing: 0; border-radius: 8px; overflow: hidden; border: 1px solid #e0e0e0; font-size: 0.85rem; margin: 1em 0; }
.dfc-tiptap .ProseMirror th { background-color: #254A73; color: #fff; font-weight: 600; padding: 8px 12px; text-align: center; white-space: nowrap; border-bottom: 1px solid #e0e0e0; }
.dfc-tiptap .ProseMirror td { padding: 8px 12px; text-align: center; border-bottom: 1px solid #f0f0f0; white-space: pre-line; vertical-align: top; }
.dfc-tiptap .ProseMirror tr:nth-of-type(even) td { background-color: #f8f9fa; }
`;

const MenuButton = ({ onClick, isActive, disabled, children, title }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      {/* Span wrapper keeps the tooltip working when the button is disabled */}
      <span className="inline-flex">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClick}
          disabled={disabled}
          className={cn(
            'h-8 w-8 text-muted-foreground',
            isActive && 'bg-brand-buttonActive/15 text-brand-buttonActive'
          )}
        >
          {children}
        </Button>
      </span>
    </TooltipTrigger>
    <TooltipContent side="top">{title}</TooltipContent>
  </Tooltip>
);

// Base icon with a small red X overlay — used for delete-row / delete-column
// so they are visually distinct from their add counterparts.
const DeleteOverlayIcon = ({ icon: Icon }) => (
  <span className="relative inline-flex">
    <Icon />
    <X
      className="absolute -right-1.5 -top-1 !h-3 !w-3 text-destructive"
      strokeWidth={3}
      aria-hidden="true"
    />
  </span>
);

const ToolbarSeparator = () => (
  <Separator orientation="vertical" className="mx-1 h-6" />
);

const MenuBar = ({ editor }) => {
  // Link dialog (replaces window.prompt)
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  // Image dialog (replaces window.prompt)
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const imageFileRef = useRef(null);

  const openLinkDialog = useCallback(() => {
    setLinkUrl(editor?.getAttributes('link').href || '');
    setLinkDialogOpen(true);
  }, [editor]);

  const applyLink = useCallback(() => {
    const url = linkUrl.trim();
    if (url === '') {
      // Same semantics as before: empty URL removes the link
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    setLinkDialogOpen(false);
  }, [editor, linkUrl]);

  const applyImage = useCallback(() => {
    const url = imageUrl.trim();
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
    setImageDialogOpen(false);
  }, [editor, imageUrl]);

  // Upload a local file to S3 via /creator/uploadBlogImage (same endpoint as
  // the featured-image upload) and insert the returned URL at the cursor.
  const handleImageFile = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setImageUploading(true);
      try {
        const result = await uploadBlogImage(file);
        editor.chain().focus().setImage({ src: result.url }).run();
        setImageDialogOpen(false);
        toast.success('Image uploaded');
      } catch (err) {
        toast.error(err.response?.data?.detail || err.message || 'Failed to upload image');
      } finally {
        setImageUploading(false);
        if (imageFileRef.current) imageFileRef.current.value = '';
      }
    },
    [editor]
  );

  if (!editor) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 rounded-t-[inherit] border-b border-borderGrey bg-neutral-50 p-1.5">
        {/* History */}
        <MenuButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo (Ctrl+Z)"
        >
          <Undo2 />
        </MenuButton>

        <MenuButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo (Ctrl+Shift+Z)"
        >
          <Redo2 />
        </MenuButton>

        <ToolbarSeparator />

        {/* Text formatting */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold (Ctrl+B)"
        >
          <Bold />
        </MenuButton>

        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic (Ctrl+I)"
        >
          <Italic />
        </MenuButton>

        <ToolbarSeparator />

        {/* Headings */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          <Heading1 />
        </MenuButton>

        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          <Heading2 />
        </MenuButton>

        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
        >
          <Heading3 />
        </MenuButton>

        <ToolbarSeparator />

        {/* Lists */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List />
        </MenuButton>

        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered />
        </MenuButton>

        <ToolbarSeparator />

        {/* Blocks */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Quote"
        >
          <Quote />
        </MenuButton>

        <MenuButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          isActive={editor.isActive('codeBlock')}
          title="Code Block"
        >
          <Code />
        </MenuButton>

        <ToolbarSeparator />

        {/* Links & Images */}
        <MenuButton
          onClick={openLinkDialog}
          isActive={editor.isActive('link')}
          title="Add Link"
        >
          <LinkIcon />
        </MenuButton>

        <MenuButton
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={!editor.isActive('link')}
          title="Remove Link"
        >
          <Unlink />
        </MenuButton>

        <MenuButton onClick={() => setImageDialogOpen(true)} title="Add Image">
          <ImageIcon />
        </MenuButton>

        <ToolbarSeparator />

        {/* Tables */}
        <MenuButton
          onClick={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          }
          disabled={editor.isActive('table')}
          title="Insert Table"
        >
          <TableIcon />
        </MenuButton>

        {editor.isActive('table') && (
          <>
            <MenuButton
              onClick={() => editor.chain().focus().addRowAfter().run()}
              title="Add Row"
            >
              <BetweenHorizontalEnd />
            </MenuButton>

            <MenuButton
              onClick={() => editor.chain().focus().deleteRow().run()}
              title="Delete Row"
            >
              <DeleteOverlayIcon icon={Rows3} />
            </MenuButton>

            <MenuButton
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              title="Add Column"
            >
              <BetweenVerticalEnd />
            </MenuButton>

            <MenuButton
              onClick={() => editor.chain().focus().deleteColumn().run()}
              title="Delete Column"
            >
              <DeleteOverlayIcon icon={Columns3} />
            </MenuButton>

            <MenuButton
              onClick={() => editor.chain().focus().deleteTable().run()}
              title="Delete Table"
            >
              <Trash2 className="text-destructive" />
            </MenuButton>
          </>
        )}
      </div>

      {/* Link dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>{editor.isActive('link') ? 'Edit Link' : 'Add Link'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="tiptap-link-url">URL</Label>
            <Input
              id="tiptap-link-url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyLink()}
              placeholder="https://…"
              autoFocus
            />
            <p className="text-token-base text-muted-foreground">
              Leave empty to remove the link.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setLinkDialogOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={applyLink}>
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image dialog */}
      <Dialog
        open={imageDialogOpen}
        onOpenChange={(o) => {
          setImageDialogOpen(o);
          if (o) setImageUrl('');
        }}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Add Image</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <input
              ref={imageFileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={handleImageFile}
            />
            <Button
              size="sm"
              onClick={() => imageFileRef.current?.click()}
              disabled={imageUploading}
            >
              {imageUploading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {imageUploading ? 'Uploading…' : 'Upload image'}
            </Button>
            <p className="text-center text-token-sm text-muted-foreground">
              JPG, PNG, WebP or GIF, max 5MB — or paste a URL below
            </p>
            <div className="grid gap-1.5">
              <Label htmlFor="tiptap-image-url">Image URL</Label>
              <Input
                id="tiptap-image-url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyImage()}
                placeholder="https://…"
                disabled={imageUploading}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setImageDialogOpen(false)}
              disabled={imageUploading}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={applyImage} disabled={!imageUrl.trim() || imageUploading}>
              Insert URL
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default function TipTapEditor({
  content,
  onChange,
  placeholder = 'Start writing your blog post...',
  minHeight = 400,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: content || { type: 'doc', content: [] },
    onUpdate: ({ editor }) => {
      onChange(editor.getJSON());
    },
  });

  // Update editor content when prop changes (e.g., when editing a different post)
  useEffect(() => {
    if (editor && content) {
      // Only update if content is different to avoid cursor position issues
      const currentContent = JSON.stringify(editor.getJSON());
      const newContent = JSON.stringify(content);
      if (currentContent !== newContent) {
        editor.commands.setContent(content);
      }
    }
  }, [editor, content]);

  return (
    <div
      className="dfc-tiptap flex flex-col rounded-token-medium border border-borderGrey bg-white"
      style={{ minHeight, '--dfc-editor-min-height': `${Math.max(minHeight - 100, 120)}px` }}
    >
      <style>{EDITOR_STYLES}</style>
      <MenuBar editor={editor} />
      <div className="flex-1 overflow-auto p-4">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
